import { getInitials } from '@/utils/userNames'
import Modal from '@components/Modal'
import MultiUserPickerModal from '@components/MultiUserPickerModal'
import { GetUserType } from '@orbit/api-types'
import React, { useEffect, useState } from 'react'
import {
    AvatarStack,
    Button,
    ButtonGroup,
    CategoryList,
    CategoryMain,
    CategoryRow,
    CategorySubtitle,
    CategoryTitle,
    Chevron,
    Description,
    StackAvatar,
    StackOverflow,
} from './ActivityFilterModal.styles'
import { ActivityFilter, EMPTY_ACTIVITY_FILTER } from './activityFilter'

interface ActivityFilterModalProps {
    isOpen: boolean
    onClose: () => void
    onApply: (filter: ActivityFilter) => void
    members: GetUserType[]
    currentUserId: number | undefined
    currentFilter: ActivityFilter
}

type Category = 'paidBy' | 'owes' | 'payUp'

const CATEGORY_TITLES: Record<Category, string> = {
    paidBy: 'Paid for an Expense',
    owes: 'Owes in an Expense',
    payUp: 'Involved in a Pay Up',
}

const PICKER_TITLES: Record<Category, string> = {
    paidBy: 'Select who paid for the expense',
    owes: 'Select who owes in the expense',
    payUp: 'Select who is involved in the payup',
}

const MAX_STACK = 3

const SelectionSummary: React.FC<{ ids: number[]; members: GetUserType[] }> = ({ ids, members }) => {
    if (ids.length === 0) return <>Anyone</>
    const selected = ids
        .map(id => members.find(m => m.id === id))
        .filter((m): m is GetUserType => !!m)
    const shown = selected.slice(0, MAX_STACK)
    const overflow = selected.length - shown.length
    return (
        <>
            <AvatarStack>
                {shown.map(m => (
                    <StackAvatar key={m.id}>
                        {m.profilePicture ? (
                            <img src={m.profilePicture} alt={getInitials(m)} />
                        ) : (
                            getInitials(m)
                        )}
                    </StackAvatar>
                ))}
            </AvatarStack>
            <span>
                {selected.length} selected
                {overflow > 0 && <StackOverflow>+{overflow}</StackOverflow>}
            </span>
        </>
    )
}

const ActivityFilterModal: React.FC<ActivityFilterModalProps> = ({
    isOpen,
    onClose,
    onApply,
    members,
    currentUserId,
    currentFilter,
}) => {
    const [filter, setFilter] = useState<ActivityFilter>(currentFilter)
    const [openPicker, setOpenPicker] = useState<Category | null>(null)

    useEffect(() => {
        if (isOpen) {
            setFilter(currentFilter)
            setOpenPicker(null)
        }
    }, [isOpen, currentFilter])

    const handleApply = () => {
        onApply(filter)
    }

    const handleClear = () => {
        setFilter(EMPTY_ACTIVITY_FILTER)
    }

    const idsForCategory = (cat: Category): number[] => {
        if (cat === 'paidBy') return filter.paidByUserIds
        if (cat === 'owes') return filter.owesUserIds
        return filter.payUpUserIds
    }

    const handlePickerConfirm = (ids: number[]) => {
        if (openPicker === 'paidBy') {
            setFilter(prev => ({ ...prev, paidByUserIds: ids }))
        } else if (openPicker === 'owes') {
            setFilter(prev => ({ ...prev, owesUserIds: ids }))
        } else if (openPicker === 'payUp') {
            setFilter(prev => ({ ...prev, payUpUserIds: ids }))
        }
        setOpenPicker(null)
    }

    const renderRow = (cat: Category): React.ReactNode => {
        const ids = idsForCategory(cat)
        const active = ids.length > 0
        return (
            <CategoryRow type="button" onClick={() => setOpenPicker(cat)}>
                <CategoryMain>
                    <CategoryTitle>{CATEGORY_TITLES[cat]}</CategoryTitle>
                    <CategorySubtitle $active={active}>
                        <SelectionSummary ids={ids} members={members} />
                    </CategorySubtitle>
                </CategoryMain>
                <Chevron>›</Chevron>
            </CategoryRow>
        )
    }

    return (
        <>
            <Modal
                isOpen={isOpen && openPicker === null}
                onClose={onClose}
                title="Filter"
                size="medium"
                backgroundColor="#fff8f6"
                closeButtonColor="#a03c04"
                footer={
                    <ButtonGroup>
                        <Button type="button" $variant="ghost" onClick={handleClear}>
                            Clear All
                        </Button>
                        <Button type="button" $variant="primary" onClick={handleApply}>
                            Apply Filters
                        </Button>
                    </ButtonGroup>
                }
            >
                <Description>
                    Pick members for any of the categories below. Results show items matching any
                    category.
                </Description>
                <CategoryList>
                    {renderRow('paidBy')}
                    {renderRow('owes')}
                    {renderRow('payUp')}
                </CategoryList>
            </Modal>

            <MultiUserPickerModal
                isOpen={openPicker !== null}
                onClose={() => setOpenPicker(null)}
                onConfirm={handlePickerConfirm}
                title={openPicker ? PICKER_TITLES[openPicker] : ''}
                users={members}
                currentUserId={currentUserId}
                initiallySelectedUserIds={openPicker ? idsForCategory(openPicker) : []}
            />
        </>
    )
}

export default ActivityFilterModal
